import { Grid, TextField, Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import { Form, Formik } from "formik";
import React from "react";
import BriaLogo from "../assets/icons/BriaLogo";
import BriaButton, { BUTTONTYPES } from "../Components/UI/BriaButton";
import Footer from "../Components/UI/Footer";
import * as Yup from "yup";
import ErrorIcon from "../assets/icons/ErrorIcon";
import { useTranslation } from "react-i18next";
import duck from "../assets/duck.png";
import { getDatabase, push, ref } from "firebase/database";
import Countdown, { zeroPad } from "react-countdown";
import Analytics, { ANALYTICS_EVENTS } from "../Models/Analytics";

const useStyles = makeStyles((theme) => ({
	rootContainer: {
		backgroundImage: "linear-gradient(#E8E8E8, #E8E8E8)",
		height: "100%",
	},
	rootGrid: {
		height: "100%",
		overflow: "hidden",
	},
	grid: {
		paddingLeft: "5%",
		[theme.breakpoints.down("sm")]: {
			padding: "5%",
		},
		[theme.breakpoints.up(1921)]: {
			padding: 0,
		},
	},
	contentGrid: {
		maxWidth: 1920,
		margin: "0 auto",
		[theme.breakpoints.up("sm")]: {
			minHeight: "80%",
		},
	},
	innerContentGrid: {
		height: "100%",
		alignItems: "center",
	},
	footerGrid: {
		flexGrow: 1,
	},
	logo: {
		height: "100%",
		width: 132,
		[theme.breakpoints.down("sm")]: {
			width: 68,
		},
		[theme.breakpoints.up(1921)]: {
			width: 200,
		},
	},
	title: {
		color: "#5300C9",
		fontSize: 55,
		lineHeight: 1.2,
		[theme.breakpoints.down("sm")]: {
			fontSize: 28,
			marginTop: "8%",
		},
		[theme.breakpoints.up(1921)]: {
			fontSize: 80,
		},
	},
	thanksTitle: {
		fontSize: 55,
		[theme.breakpoints.down("sm")]: {
			fontSize: 40,
		},
		[theme.breakpoints.up(1921)]: {
			fontSize: 80,
		},
	},
	description: {
		maxWidth: "80%",
		color: "#505050",
		fontSize: 20,
		paddingTop: 10,
		[theme.breakpoints.down("sm")]: {
			fontSize: 16,
		},
		[theme.breakpoints.up(1921)]: {
			fontSize: 28,
		},
	},
	thanksDescription: {
		maxWidth: "80%",
		color: "#5300C9",
		fontSize: 30,
		paddingTop: 10,
		[theme.breakpoints.down("sm")]: {
			fontSize: 28,
		},
		[theme.breakpoints.up(1921)]: {
			fontSize: 38,
		},
	},
	countdownContainer: {
		[theme.breakpoints.down("sm")]: {
			textAlign: "center",
		},
		"& div": {
			[theme.breakpoints.down("sm")]: {
				display: "inline-block",
				textAlign: "left",
			},
		},
	},
	countdownDescription: {
		fontSize: 30,
		[theme.breakpoints.up(1921)]: {
			fontSize: 38,
		},
		[theme.breakpoints.up("sm")]: {
			paddingTop: "10%",
		},
	},
	countdown: {
		fontSize: 30,
		fontWeight: "bold",
		[theme.breakpoints.up(1921)]: {
			fontSize: 38,
		},
	},
	underlined: {
		textDecoration: "underline",
		textUnderlineOffset: "5px",
		textDecorationColor: "rgba(80,80,80,0.1)",
		textDecorationThickness: "5px",
		[theme.breakpoints.up(1921)]: {
			textUnderlineOffset: "8px",
			textDecorationThickness: "8px",
		},
	},
	email: {
		marginTop: "2%",
		maxWidth: "65%",
		"& input": {
			fontSize: 25,
			[theme.breakpoints.up(1921)]: {
				fontSize: 30,
			},
			[theme.breakpoints.down("sm")]: {
				fontSize: 18,
			},
		},
		[theme.breakpoints.down("sm")]: {
			maxWidth: "100%",
		},
		[theme.breakpoints.up(1921)]: {
			maxWidth: "80%",
		},
	},
	submitButton: {
		"&:disabled": {
			color: "rgba(216, 0, 103, 0.45) !important",
			opacity: 1,
		},
		border: "none !important",
		borderRadius: 20,
		backgroundColor: "white",
		boxShadow: "10px 10px 10px 0px #c7d0e0",
		marginTop: "4%",
		maxWidth: "45%",
		fontSize: 20,
		[theme.breakpoints.down("sm")]: {
			maxWidth: "100%",
			fontSize: 20,
		},
		[theme.breakpoints.up(1921)]: {
			fontSize: 28,
			maxWidth: "60%",
		},
	},
	duckImgContainer: {
		marginTop: "5%",
		"& img": {
			marginLeft: "33.5%",
		},
	},
	error: {
		fontSize: 16,
		fontWeight: theme.typography.fontWeightBold,
		maxWidth: 440,
		marginBottom: 15,
		marginLeft: 12,
		[theme.breakpoints.up(1921)]: {
			fontSize: 32,
		},
	},
	errorIcon: {
		fontSize: 16,
		marginRight: 4,
		verticalAlign: "middle",
		[theme.breakpoints.up(1921)]: {
			fontSize: 32,
		},
	},
}));

function EarlyBird() {
	const [errorMessage, setErrorMessage] =
		React.useState<boolean | string>(false);
	const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
	const countDownToDate = new Date("2022-12-01T10:00:00.000+02:00");
	const { t } = useTranslation();
	const classes = useStyles();
	const initialValues = {
		email: "",
	};
	const handleSubmit = async (values: any) => {
		try {
			Analytics.logEvent(ANALYTICS_EVENTS.EARLY_BIRD_SIGN_UP);
			const db = getDatabase();
			await push(ref(db, "launch_registrants/"), values.email);
			setIsSubmitted(true);
		} catch (error: any) {
			setErrorMessage(error.code);
		}
	};

	const duckImg = () => <img width="100%" src={duck}></img>;

	const isMobile = window.innerWidth <= 768;

	return (
		<Box className={classes.rootContainer}>
			<Grid container direction="column" className={classes.rootGrid}>
				<Grid item className={`${classes.grid} ${classes.contentGrid}`}>
					<Grid container className={classes.innerContentGrid}>
						<Grid item xs={12}>
							<BriaLogo className={classes.logo} />
						</Grid>
						<Grid item xs={isMobile ? 12 : 5}>
							{isSubmitted ? (
								<>
									<Typography
										className={`${classes.title} ${classes.thanksTitle}`}
									>
										Thanks for<br></br>
										signing up!
									</Typography>
									<Typography
										className={classes.thanksDescription}
									>
										Keep an eye out for an email on launch
										day.
									</Typography>
									{isMobile && (
										<Box
											className={classes.duckImgContainer}
										>
											{duckImg()}
										</Box>
									)}
									<Box className={classes.countdownContainer}>
										<Box>
											<Typography
												className={
													classes.countdownDescription
												}
											>
												Countdown to launch:
											</Typography>
											<Countdown
												date={countDownToDate}
												renderer={({
													days,
													hours,
													minutes,
													seconds,
													completed,
												}) => (
													<Typography
														className={
															classes.countdown
														}
													>
														<span
															className={
																classes.underlined
															}
														>
															{zeroPad(days)}
														</span>
														:
														<span
															className={
																classes.underlined
															}
														>
															{zeroPad(hours)}
														</span>
														:
														<span
															className={
																classes.underlined
															}
														>
															{zeroPad(minutes)}
														</span>
														:
														<span
															className={
																classes.underlined
															}
														>
															{zeroPad(seconds)}
														</span>
													</Typography>
												)}
											/>
										</Box>
									</Box>
								</>
							) : (
								<>
									<Typography className={classes.title}>
										Early birds,<br></br>
										places are limited.
									</Typography>
									<Typography className={classes.description}>
										Create the perfect visuals for your
										brand in seconds with the free Bria
										beta. Be sure to get a spot - register
										for the pre-release email.
									</Typography>
									{isMobile && (
										<Box
											className={classes.duckImgContainer}
										>
											{duckImg()}
										</Box>
									)}
									<Formik
										initialValues={initialValues}
										isInitialValid={false}
										validationSchema={Yup.object().shape({
											email: Yup.string()
												.email(t("invalidEmail"))
												.required("Email is required"),
										})}
										onSubmit={handleSubmit}
									>
										{({
											values,
											errors,
											handleChange,
											isValid,
										}) => (
											<Form>
												<Box
													display="flex"
													flexDirection="column"
													alignItems="left"
												>
													<TextField
														id="email"
														className={
															classes.email
														}
														placeholder="Email"
														onChange={handleChange}
														value={values.email}
														error={!!errors.email}
														helperText={
															errors.email
														}
													/>

													{errorMessage && (
														<Typography
															className={
																classes.error
															}
															color="secondary"
														>
															<ErrorIcon
																className={
																	classes.errorIcon
																}
															/>
															{t(
																`${errorMessage}`
															)}
														</Typography>
													)}

													<BriaButton
														buttonType={
															BUTTONTYPES.SECONDARY
														}
														className={
															classes.submitButton
														}
														type="submit"
														disabled={!isValid}
													>
														Register for Free
													</BriaButton>
												</Box>
											</Form>
										)}
									</Formik>
								</>
							)}
						</Grid>
						{!isMobile && (
							<Grid item xs={7}>
								{duckImg()}
							</Grid>
						)}
					</Grid>
				</Grid>
				<Grid item className={classes.footerGrid}>
					<Footer />
				</Grid>
			</Grid>
		</Box>
	);
}

export default EarlyBird;
